<template>
  <div class="service-health">
    <div class="service-health-header">
      <h1>Service Health Monitoring</h1>
      <div class="header-actions">
        <div data-test="last-update" class="last-update text-body-2">
          <span v-if="lastUpdated">
            Last updated: {{ formatTime(lastUpdated) }}
          </span>
        </div>
        <v-btn
          data-test="refresh-button"
          color="primary"
          @click="refreshData"
          :loading="loading"
        >
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>
    </div>

    <!-- Critical Alert -->
    <v-alert
      v-if="criticalServicesDown.length > 0"
      data-test="critical-alert"
      type="error"
      prominent
      class="mb-4"
    >
      <v-icon>mdi-alert-circle</v-icon>
      Critical service{{ criticalServicesDown.length > 1 ? 's' : '' }} down: 
      {{ criticalServicesDown.join(', ') }}
    </v-alert>

    <!-- Summary Statistics -->
    <v-card class="mb-4">
      <v-card-text>
        <div data-test="health-summary" class="health-summary">
          <div class="summary-item">
            <v-icon color="success">mdi-check-circle</v-icon>
            <span>{{ healthySummary.healthy }} Healthy</span>
          </div>
          <div class="summary-item">
            <v-icon color="warning">mdi-alert-circle</v-icon>
            <span>{{ healthySummary.degraded }} Degraded</span>
          </div>
          <div class="summary-item">
            <v-icon color="error">mdi-close-circle</v-icon>
            <span>{{ healthySummary.down }} Down</span>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Status Filter -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-select
          v-model="statusFilter"
          data-test="status-filter"
          :items="statusOptions"
          label="Filter by status"
          clearable
          outlined
          dense
        />
      </v-col>
    </v-row>

    <!-- Loading Overlay -->
    <v-overlay
      v-if="loading"
      data-test="loading-overlay"
      :value="true"
      opacity="0.8"
    >
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <!-- Service Cards -->
    <v-row>
      <v-col
        v-for="service in filteredServices"
        :key="service.name"
        cols="12"
        md="6"
        lg="4"
      >
        <v-card
          data-test="service-card"
          :class="['service-card', `status-${service.status}`]"
          @click="showServiceDetails(service)"
        >
          <v-card-title class="d-flex justify-space-between align-center">
            <span>{{ formatServiceName(service.name) }}</span>
            <v-chip
              :color="getStatusColor(service.status)"
              :data-test="`status-${service.status}`"
              small
            >
              {{ service.status }}
            </v-chip>
          </v-card-title>
          
          <v-card-text>
            <div v-if="service.response_time_ms !== null" class="metric">
              <span class="metric-label">Response Time:</span>
              <span
                data-test="response-time"
                :class="getResponseTimeClass(service.response_time_ms)"
              >
                {{ service.response_time_ms }}ms
              </span>
            </div>
            
            <div v-if="service.error" class="error-section">
              <span class="metric-label">Error:</span>
              <span data-test="error-message" class="error-message">
                {{ service.error }}
              </span>
            </div>
            
            <div class="metric">
              <span class="metric-label">Last Check:</span>
              <span>{{ formatTime(service.last_check) }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Service Detail Modal -->
    <v-dialog
      v-model="detailDialog"
      max-width="600"
      data-test="service-detail-modal"
    >
      <v-card v-if="selectedService">
        <v-card-title>
          {{ formatServiceName(selectedService.name) }} Details
        </v-card-title>
        <v-card-text>
          <div class="detail-item">
            <strong>Status:</strong> {{ selectedService.status }}
          </div>
          <div class="detail-item">
            <strong>Last Check:</strong> {{ formatTime(selectedService.last_check) }}
          </div>
          <div v-if="selectedService.response_time_ms" class="detail-item">
            <strong>Response Time:</strong> {{ selectedService.response_time_ms }}ms
          </div>
          <div v-if="selectedService.error" class="detail-item">
            <strong>Error:</strong> {{ selectedService.error }}
          </div>
          <!-- Additional details would be loaded from fetchServiceDetails -->
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="detailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useHealthStore } from '@/store/modules/health'
import { SERVICE_HEALTH_REFRESH_INTERVAL, ServiceStatus } from '@/constants'

const healthStore = useHealthStore()

// Reactive data
const statusFilter = ref('')
const detailDialog = ref(false)
const selectedService = ref(null)
let refreshInterval = null

// Computed properties
const services = computed(() => healthStore.services)
const loading = computed(() => healthStore.loading)
const error = computed(() => healthStore.error)
const lastUpdated = computed(() => healthStore.lastUpdated)
const criticalServicesDown = computed(() => healthStore.criticalServicesDown)

const healthySummary = computed(() => {
  const summary = {
    healthy: 0,
    degraded: 0,
    down: 0
  }
  
  services.value.forEach(service => {
    if (service.status === ServiceStatus.HEALTHY) summary.healthy++
    else if (service.status === ServiceStatus.DEGRADED) summary.degraded++
    else if (service.status === ServiceStatus.DOWN) summary.down++
  })
  
  return summary
})

const filteredServices = computed(() => {
  if (!statusFilter.value) return services.value
  return services.value.filter(service => service.status === statusFilter.value)
})

// Data
const statusOptions = [
  { text: 'All', value: '' },
  { text: 'Healthy', value: 'healthy' },
  { text: 'Degraded', value: 'degraded' },
  { text: 'Down', value: 'down' }
]

// Methods
const refreshData = async () => {
  await healthStore.fetchServiceHealth()
}

const getStatusColor = (status) => {
  const colors = {
    healthy: 'success',
    degraded: 'warning',
    down: 'error'
  }
  return colors[status] || 'grey'
}

const getResponseTimeClass = (responseTime) => {
  if (!responseTime) return 'text-grey'
  if (responseTime < 100) return 'text-success'
  if (responseTime < 500) return 'text-warning'
  return 'text-error'
}

const formatServiceName = (name) => {
  return name
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const formatTime = (timeString) => {
  if (!timeString) return '-'
  const date = new Date(timeString)
  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }).format(date)
}

const showServiceDetails = (service) => {
  selectedService.value = service
  detailDialog.value = true
}

// Setup auto-refresh
const setupAutoRefresh = () => {
  refreshInterval = setInterval(() => {
    refreshData()
  }, SERVICE_HEALTH_REFRESH_INTERVAL)
}

// Lifecycle
onMounted(() => {
  refreshData()
  setupAutoRefresh()
})

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})
</script>

<style scoped>
.service-health {
  padding: 24px;
}

.service-health-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.last-update {
  color: #666;
}

.health-summary {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 16px 0;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
}

.service-card {
  cursor: pointer;
  transition: all 0.3s;
}

.service-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.service-card.status-down {
  border-left: 4px solid #f44336;
}

.service-card.status-degraded {
  border-left: 4px solid #ff9800;
}

.service-card.status-healthy {
  border-left: 4px solid #4caf50;
}

.metric {
  margin: 8px 0;
}

.metric-label {
  font-weight: 500;
  margin-right: 8px;
}

.error-section {
  margin: 12px 0;
  padding: 8px;
  background-color: #ffebee;
  border-radius: 4px;
}

.error-message {
  color: #c62828;
  font-size: 0.9rem;
}

.text-success {
  color: #4caf50;
  font-weight: 600;
}

.text-warning {
  color: #ff9800;
  font-weight: 600;
}

.text-error {
  color: #f44336;
  font-weight: 600;
}

.detail-item {
  margin: 8px 0;
}
</style>